import type { ComplianceNotePublic } from "@common/client"
import { ComplianceNoteService } from "@common/client"
import { type UseQueryOptions, useIsMutating } from "@tanstack/react-query"
import { quoteBaseKey } from "./quoting/quote"
import { makeAxiosMutation, unwrap } from "./utils"
import { workPackageBaseKey } from "./work-packages"

export const complianceNoteBaseKey = ["compliance-note"]

export const complianceNoteLatestKey = (workTaskId?: string) => [
  ...complianceNoteBaseKey,
  "latest",
  ...(workTaskId ? [workTaskId] : []),
]

export const complianceNoteLatestQuery = (
  params: Parameters<
    typeof ComplianceNoteService.getLatestComplianceNoteByWorkTaskId
  >[0],
): UseQueryOptions<ComplianceNotePublic | null> => ({
  queryFn: async () => {
    try {
      const result =
        await ComplianceNoteService.getLatestComplianceNoteByWorkTaskId(
          params,
        ).then(unwrap)
      return result ?? null
    } catch (err: unknown) {
      if (
        err &&
        typeof err === "object" &&
        "status" in err &&
        (err as { status: number }).status === 404
      ) {
        return null
      }
      throw err
    }
  },
  queryKey: complianceNoteLatestKey(params.work_task_id),
})

const complianceNoteWriteKey = [...complianceNoteBaseKey, "write"]

export const useComplianceNoteSaving = (): boolean =>
  useIsMutating({ mutationKey: complianceNoteWriteKey }) > 0

export const useUpdateComplianceNoteMutation = makeAxiosMutation({
  serviceCall: ComplianceNoteService.updateItem,
  mutationKey: complianceNoteWriteKey,
  invalidates: () => [
    complianceNoteLatestKey(),
    quoteBaseKey,
    workPackageBaseKey,
  ],
})

export const useCreateComplianceNoteMutation = makeAxiosMutation({
  serviceCall: ComplianceNoteService.createItem,
  mutationKey: complianceNoteWriteKey,
  invalidates: () => [
    complianceNoteLatestKey(),
    quoteBaseKey,
    workPackageBaseKey,
  ],
})
